import { z } from 'zod';
import {
  AllCharLabelSchema,
  GradeSchema,
  SubjectSchema,
  UUIDSchema,
  XeroCodeSchema,
} from '../common/schemas';

/**
 * Shared & Helper Schemas
 */

export const DBCourseSubjectCourseIdSchema = z.string().refine(val => {
  const parts = val.split('#');

  if (parts.length !== 2 && parts.length !== 3) return false;

  return (
    SubjectSchema.safeParse(parts[0]).success &&
    DBCourseCourseIdSchema.safeParse(parts[1]).success &&
    // Either there are only two parts (no stream ID), or the third part is a valid stream ID
    (parts.length === 2 || UUIDSchema.safeParse(parts[2]).success)
  );
});

/**
 * DB Schemas
 */

export const DBCourseCourseIdSchema = z
  .string()
  .length(6)
  .refine(val => {
    const subject = val.slice(0, 4);
    const year = val.slice(4, 6);

    return SubjectSchema.safeParse(subject).success && /^[0-9]{2}$/.test(year);
  });
export const DBCourseCreditsPerTermSchema = z.number().int().min(0).max(100);
export const DBCourseGradeSchema = GradeSchema;
export const DBCourseLabelSchema = AllCharLabelSchema.min(1).max(64);
export const DBCourseShortLabelSchema = AllCharLabelSchema.min(1).max(16);
export const DBCourseSubjectSchema = SubjectSchema;
export const DBCourseTermsSchema = z.number().int().min(1).max(100);
export const DBCourseXeroCodesSchema = z
  .object({
    CREDIT: XeroCodeSchema,
    TERM: XeroCodeSchema,
    EXTRA: XeroCodeSchema,
  })
  .strict();

// `terms`, `creditsPerTerm` and `xeroCodes` are the billing values a course state no class
// governs is priced by (see getCourseFallbackPriceValues in @repo/utils/prices). Nothing
// writes them: a course is created and updated without them, and one that carries none
// prices no such course state.
export const DBCourseSchema = z.object({
  courseId: DBCourseCourseIdSchema,
  creditsPerTerm: DBCourseCreditsPerTermSchema.optional(),
  grade: DBCourseGradeSchema,
  label: DBCourseLabelSchema,
  shortLabel: DBCourseShortLabelSchema,
  subject: DBCourseSubjectSchema,
  terms: DBCourseTermsSchema.optional(),
  xeroCodes: DBCourseXeroCodesSchema.optional(),
});

/**
 * Client Schemas
 */

export const CourseCourseIdSchema = DBCourseCourseIdSchema;
export const CourseCreditsPerTermSchema = DBCourseCreditsPerTermSchema;
export const CourseGradeSchema = DBCourseGradeSchema;
export const CourseLabelSchema = DBCourseLabelSchema;
export const CourseShortLabelSchema = DBCourseShortLabelSchema;
export const CourseSubjectSchema = DBCourseSubjectSchema;
export const CourseTermsSchema = DBCourseTermsSchema;
export const CourseXeroCodesSchema = DBCourseXeroCodesSchema;

export const CourseSchema = z.object({
  courseId: CourseCourseIdSchema.meta({ title: 'Course ID' }),
  creditsPerTerm: CourseCreditsPerTermSchema.optional(),
  grade: CourseGradeSchema.meta({ title: 'Grade' }),
  label: CourseLabelSchema.meta({ title: 'Label' }),
  shortLabel: CourseShortLabelSchema.meta({ title: 'Short label' }),
  subject: CourseSubjectSchema.meta({ title: 'Subject' }),
  terms: CourseTermsSchema.optional(),
  xeroCodes: CourseXeroCodesSchema.optional(),
});
